import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Order, InventoryItem, Department, ApiResponse } from '../../lib/types';
import { Modal } from '../ui';
import { inventoryMerge, markOrderStocked } from '../../lib/api';

interface StockModalProps {
  order: Order;
  inventory: InventoryItem[];
  departments: Department[];
  onInventoryAdd: (payload: {
    item_name: string; sku?: string; size?: string;
    quantity?: number; target_department_id?: string;
  }) => Promise<ApiResponse<InventoryItem>>;
  onInventoryChanged: () => void;
  onOrderChanged: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  onClose: () => void;
}

const ITEMS_PER_PAGE = 5;

export function StockModal({
  order, inventory, departments,
  onInventoryAdd, onInventoryChanged, onOrderChanged, showToast, onClose,
}: StockModalProps) {
  const items = order.order_items ?? [];

  // default all items checked
  const [checked, setChecked] = useState<Set<number>>(() => new Set(items.map((_, i) => i)));
  const [mergeTargets, setMergeTargets] = useState<Record<number, string>>({});
  const [dropOpen, setDropOpen] = useState<number | null>(null);
  const [dropAbove, setDropAbove] = useState(false);
  const [dropPortalRect, setDropPortalRect] = useState<{ top: number; bottom: number; left: number; width: number } | null>(null);
  const [dropSearches, setDropSearches] = useState<Record<number, string>>({});
  const dropContainerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [page, setPage] = useState(1);
  const [tooltip, setTooltip] = useState<{ text: string; top: number; left: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current); };
  }, []);

  const deptMap: Record<string, string> = {};
  departments.forEach(d => { if (d.id) deptMap[d.id] = d.name; });

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const pageOffset = (page - 1) * ITEMS_PER_PAGE;
  const pageItems = items.slice(pageOffset, pageOffset + ITEMS_PER_PAGE);

  const toggleItem = (idx: number) => {
    setChecked(prev => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };

  const handleConfirm = async () => {
    const selected = items.map((_, i) => i).filter(i => checked.has(i));
    if (selected.length === 0) {
      showToast('Please select at least one item');
      return;
    }
    setBusy(true);
    let added = 0;
    let failed = 0;
    let mergeFailed = 0;

    for (const idx of selected) {
      const item = items[idx];
      const mergeTargetId = mergeTargets[idx];
      const addRes = await onInventoryAdd({
        item_name: item.item_name,
        sku: item.code || undefined,
        size: item.size || undefined,
        quantity: item.quantity || 1,
        target_department_id: order.department_id,
      });
      if (!addRes.ok) { failed++; continue; }
      if (mergeTargetId && addRes.data) {
        const newItemId = (addRes.data as any).id;
        if (newItemId) {
          const mergeRes = await inventoryMerge(newItemId, mergeTargetId);
          if (!mergeRes.ok) mergeFailed++;
        }
      }
      added++;
    }

    // Only mark the order stocked if every selected line actually landed in
    // inventory. A partial success leaves the order reopen-able so the user
    // has a UI path to retry the failed items — canStock() in DashboardTab
    // keys off !stocked_at, so once set the three-dot action disappears.
    const fullyStocked = failed === 0 && added === selected.length;
    if (fullyStocked) {
      const stockRes = await markOrderStocked(order.id);
      if (stockRes.ok) {
        showToast(`${added} item${added !== 1 ? 's' : ''} added to inventory`, 'success');
      } else {
        showToast(stockRes.message || 'Items added, but couldn\'t mark order stocked');
      }
      onInventoryChanged();
      onOrderChanged();
      setBusy(false);
      onClose();
      return;
    }

    // Partial failure: keep the order unstocked, tell the user exactly
    // what happened, and leave the modal open so they can uncheck the
    // successful rows and retry the rest.
    if (added > 0) {
      onInventoryChanged();
      showToast(
        `${added} added, ${failed} failed — order left open so you can retry`,
        failed === 0 ? 'success' : 'error',
      );
    } else {
      showToast('Nothing was added — please try again');
    }
    if (mergeFailed > 0) {
      showToast(`${mergeFailed} item${mergeFailed !== 1 ? 's' : ''} added but merge failed`);
    }
    setBusy(false);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Add to inventory"
      subtitle="Select items to add to stock. Optionally merge into an existing item."
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={busy || checked.size === 0}
          >
            {busy ? 'Adding…' : `Add ${checked.size} item${checked.size !== 1 ? 's' : ''} to inventory`}
          </button>
        </>
      }
    >
      {tooltip && (
        <div
          style={{
            position: 'fixed', top: tooltip.top, left: tooltip.left,
            transform: 'translateY(-100%) translateY(-6px)',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r)', padding: '6px 10px',
            fontSize: 'var(--fs-sm)', color: 'var(--text)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 100000, maxWidth: 350, whiteSpace: 'normal', pointerEvents: 'none',
          }}
        >
          {tooltip.text}
        </div>
      )}

      <div className="flex gap-4 mb-3 text-sm">
        <div>
          <span className="text-subtle">Order: </span>
          <strong>{order.order_number || order.id.slice(0, 8)}</strong>
          {order.vendor ? ` — ${order.vendor}` : ''}
        </div>
        <div>
          <span className="text-subtle">Dept: </span>
          <strong>{order.department_name || '—'}</strong>
        </div>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 'var(--sp-3)' }}>
        {pageItems.map((item, pageIdx) => {
          const idx = pageOffset + pageIdx;
          const isChecked = checked.has(idx);
          return (
            <div
              key={item.id || idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 300px',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '10px 14px',
                borderBottom: pageIdx < pageItems.length - 1 ? '1px solid var(--border)' : 'none',
                background: isChecked ? 'var(--bg)' : 'transparent',
              }}
            >
              {/* left */}
              <div className="flex items-center gap-3 min-w-0">
                <div onClick={() => toggleItem(idx)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                  <div className={`checkbox ${isChecked ? 'is-checked' : ''}`}>
                    {isChecked && (
                      <svg viewBox="0 0 12 12" width="12" height="12">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="min-w-0 flex-1" onClick={() => toggleItem(idx)}>
                  <div className="text-base font-medium text-truncate" style={{ cursor: 'pointer' }}>
                    {item.item_name || '—'}
                  </div>
                  <div className="text-xs text-subtle text-truncate">
                    {item.code ? `SKU: ${item.code}` : ''}
                    {item.code && item.size ? ' · ' : ''}
                    {item.size ? `Size: ${item.size}` : ''}
                    {!item.code && !item.size ? '—' : ''}
                  </div>
                </div>
              </div>

              {/* qty */}
              <div
                className="text-base font-medium text-right cursor-pointer"
                style={{ color: isChecked ? 'var(--text)' : 'var(--text3)', whiteSpace: 'nowrap' }}
                onClick={() => toggleItem(idx)}
              >
                Qty: {item.quantity || 1}
              </div>

              {/* merge dropdown */}
              <div
                style={{ position: 'relative' }}
                onClick={e => e.stopPropagation()}
                ref={el => { dropContainerRefs.current[idx] = el; }}
              >
                <input
                  className="input"
                  value={dropSearches[idx] ?? ''}
                  onChange={e => {
                    setDropSearches(prev => ({ ...prev, [idx]: e.target.value }));
                    setDropOpen(idx);
                    setMergeTargets(prev => { const n = { ...prev }; delete n[idx]; return n; });
                  }}
                  onFocus={() => {
                    const rect = dropContainerRefs.current[idx]?.getBoundingClientRect();
                    setDropAbove(!!rect && (window.innerHeight - rect.bottom) < 220);
                    setDropPortalRect(rect ? { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width } : null);
                    setDropOpen(idx);
                  }}
                  onBlur={() => setTimeout(() => setDropOpen(v => (v === idx ? null : v)), 150)}
                  placeholder="Add to existing…"
                  disabled={!isChecked}
                  style={{ opacity: isChecked ? 1 : .4 }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Portal dropdown — renders to document.body so .modal-body overflow:auto cannot clip it */}
      {dropOpen !== null && inventory.length > 0 && dropPortalRect && createPortal(
        <div
          className="menu"
          style={{
            position: 'fixed',
            left: dropPortalRect.left,
            width: dropPortalRect.width,
            zIndex: 1001,
            ...(dropAbove
              ? { bottom: window.innerHeight - dropPortalRect.top + 3 }
              : { top: dropPortalRect.bottom + 3 }),
            maxHeight: 200, overflowY: 'auto',
          }}
        >
          {(() => {
            const openIdx = dropOpen;
            const dropItems = inventory.filter(i =>
              !dropSearches[openIdx] || i.name.toLowerCase().includes((dropSearches[openIdx] || '').toLowerCase())
            );
            return dropItems.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: 'var(--fs-sm)', color: 'var(--text3)' }}>
                No matching items
              </div>
            ) : dropItems.map((inv, i, arr) => (
              <div
                key={inv.id}
                onMouseDown={() => {
                  setMergeTargets(prev => ({ ...prev, [openIdx]: inv.id }));
                  setDropSearches(prev => ({ ...prev, [openIdx]: inv.name }));
                  setDropOpen(null);
                  if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                  setTooltip(null);
                }}
                style={{
                  padding: '8px 12px', cursor: 'pointer',
                  borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--surface2)';
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                  tooltipTimerRef.current = setTimeout(() => setTooltip({
                    text: inv.name, top: rect.top, left: rect.left,
                  }), 300);
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.background = '';
                  if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                  setTooltip(null);
                }}
              >
                <div className="text-base font-medium text-truncate">{inv.name}</div>
                <div className="text-xs text-subtle">
                  {deptMap[inv.department_id] ? `${deptMap[inv.department_id]} · ` : ''}
                  {inv.available} in stock
                </div>
              </div>
            ));
          })()}
        </div>,
        document.body
      )}

      {checked.size < items.length && (
        <div
          className="text-sm mb-3"
          style={{
            padding: '8px 12px', background: 'var(--surface2)',
            borderRadius: 'var(--r)', border: '1px solid var(--border)',
            color: 'var(--text3)',
          }}
        >
          {checked.size} of {items.length} items selected — uncheck items that were not received or arrived damaged
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mb-2 text-sm text-muted">
          <button
            className="btn-ghost"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              background: 'none', border: 'none',
              cursor: page <= 1 ? 'default' : 'pointer',
              color: page <= 1 ? 'var(--text3)' : 'var(--accent)',
              fontFamily: 'inherit', fontSize: 'var(--fs-sm)', fontWeight: 500, padding: '2px 6px',
            }}
          >Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button
            className="btn-ghost"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              background: 'none', border: 'none',
              cursor: page >= totalPages ? 'default' : 'pointer',
              color: page >= totalPages ? 'var(--text3)' : 'var(--accent)',
              fontFamily: 'inherit', fontSize: 'var(--fs-sm)', fontWeight: 500, padding: '2px 6px',
            }}
          >Next</button>
        </div>
      )}
    </Modal>
  );
}
